import {
  Breakpoints,
  TextTag,
  styled,
} from '@volkswagen-onehub/components-core';
import {
  TextSize,
  TextWeight,
  TextLineHeight,
  TextAppearance,
} from '@vw-marketing/us-components';
import React from 'react';
import { TextBox } from '../../../Box';
import { Icon } from '../../../Icon';
import { SectionChainItem } from '../../../../fetch-data/graphql';

interface BreadcrumbsListProps {
  chain: SectionChainItem[];
  isMobile: boolean;
}

export const BreadcrumbsList = ({ chain, isMobile }: BreadcrumbsListProps) => {
  // On mobile, show only the last item
  if (isMobile && chain.length > 0) {
    const lastItem = chain[chain.length - 1];

    return (
      <BreadcrumbListContainer>
        <BreadcrumbItemContainer $isLast={true} $shouldCollapse={false}>
          <TextBox
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize12,
                fontWeight: TextWeight.bold,
                lineHeight: TextLineHeight.TextLineHeight20,
              },
              [Breakpoints.b1280]: {
                fontSize: TextSize.TextSize14,
                fontWeight: TextWeight.bold,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
            }}
            tag={TextTag.span}
            appearance={TextAppearance.copy200}
            wrapperStyle={{
              overflow: 'hidden',
              width: '100%',
              minWidth: 0,
            }}
            customStyle={(theme) => ({
              color: theme.colors.content.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              width: '100%',
            })}
          >
            {lastItem.sectionName}
          </TextBox>
        </BreadcrumbItemContainer>
      </BreadcrumbListContainer>
    );
  }

  // If more than 3 items, show only first, ellipsis, and last
  const shouldCollapse = chain.length > 3;
  const itemsToShow = shouldCollapse
    ? [chain[0], chain[chain.length - 1]]
    : chain;

  return (
    <BreadcrumbListContainer>
      {itemsToShow.map((item, index) => {
        const isLast = item === chain[chain.length - 1];
        const showEllipsis = shouldCollapse && index === 0;

        return (
          <React.Fragment key={item.sectionId}>
            <BreadcrumbItemContainer
              $isLast={isLast}
              $shouldCollapse={shouldCollapse}
            >
              <TextBox
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize12,
                    fontWeight: isLast ? TextWeight.bold : TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight20,
                  },
                  [Breakpoints.b1280]: {
                    fontSize: TextSize.TextSize14,
                    fontWeight: isLast ? TextWeight.bold : TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
                tag={TextTag.span}
                appearance={TextAppearance.copy200}
                wrapperStyle={{
                  overflow: 'hidden',
                  width: '100%',
                  minWidth: 0,
                }}
                customStyle={(theme) => ({
                  color: isLast
                    ? theme.colors.content.primary
                    : theme.colors.content.active,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  width: '100%',
                })}
              >
                {item.sectionName}
              </TextBox>
            </BreadcrumbItemContainer>
            {!isLast ? (
              <>
                {showEllipsis && (
                  <>
                    <BreadcrumbIconWrapper>
                      <Icon
                        name='ArrowRight'
                        variant='small'
                        ignoreDefaultColor={true}
                        style={(theme) => ({
                          paddingBottom: '0px',
                          color: theme.colors.content.active,
                        })}
                      />
                    </BreadcrumbIconWrapper>
                    <BreadcrumbEllipsis>...</BreadcrumbEllipsis>
                  </>
                )}
                <BreadcrumbIconWrapper>
                  <Icon
                    name='ArrowRight'
                    variant='small'
                    ignoreDefaultColor={true}
                    style={(theme) => ({
                      paddingBottom: '0px',
                      color: theme.colors.content.active,
                    })}
                  />
                </BreadcrumbIconWrapper>
              </>
            ) : null}
          </React.Fragment>
        );
      })}
    </BreadcrumbListContainer>
  );
};

const BreadcrumbListContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 4px;
  width: 100%;
  overflow: hidden; /* Prevent container from growing */
`;

const BreadcrumbItemContainer = styled.div<{ $isLast: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  min-width: 0; /* Allow flex items to shrink below their content size */

  flex-basis: auto;
  overflow: hidden;

  flex-shrink: ${({ $isLast, $shouldCollapse }) =>
    $isLast && !$shouldCollapse
      ? 1
      : 1}; /* Last item doesn't shrink, others do */

  /* Smaller max-width on mobile, except for last item */
  max-width: ${({ $isLast, $shouldCollapse }) =>
    $shouldCollapse ? '50px' : $isLast ? 'none' : '120px'};

  @media (min-width: ${Breakpoints.b560}px) {
    max-width: ${({ $isLast }) => ($isLast ? 'none' : '350px')};
  }
`;

const BreadcrumbEllipsis = styled.span`
  color: ${({ theme }) => theme.colors.content.tertiary};
  padding: 0 4px;
  display: flex;
  align-items: center;
  flex-shrink: 0; /* Don't shrink ellipsis */
`;

const BreadcrumbIconWrapper = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0; /* Don't shrink icons */
`;
